/* Enhance the existing theme image viewer; native Toggle and Swiper own its state. */
(() => {
  'use strict';
  if (!document.body.classList.contains('ak-guided-page')) return;

  const modalSelector = '[data-zoom-modal]';
  const imageTriggerSelector = '[data-ak-image-trigger]';
  const states = new Map();
  const defaults = {
    close: 'Close image viewer',
    title: 'Product images',
    hint: 'Use the arrows to browse'
  };
  let strings = defaults;
  try {
    strings = { ...defaults, ...JSON.parse(document.querySelector('#ak-guided-lightbox-strings')?.textContent || '{}') };
  } catch (_) { /* Native gallery remains usable when translations are unavailable. */ }

  const isVisible = (element) => Boolean(element && element.isConnected && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  const isOpen = (modal) => modal.isConnected && modal.classList.contains('is-active');
  const focus = (element) => { if (isVisible(element)) element.focus({ preventScroll: true }); };
  const close = (modal) => modal.querySelector('[data-zoom-modal-toggle]')?.click();

  function syncImageTriggers(state) {
    const slider = state.container?.querySelector('[data-js-product-media-slider]');
    const carousel = slider?.classList.contains('swiper-initialized');
    state.container?.querySelectorAll(imageTriggerSelector).forEach((trigger) => {
      const slide = trigger.closest('[data-zoom-media]');
      const available = slide && !slide.classList.contains('is-hidden') && isVisible(slide) && (!carousel || slide.classList.contains('swiper-slide-active'));
      const tabIndex = available ? 0 : -1;
      if (trigger.tabIndex !== tabIndex) trigger.tabIndex = tabIndex;
    });
  }

  function galleryOpener(state) {
    return [...(state.container?.querySelectorAll(`${imageTriggerSelector}, [data-js-zoom-button]`) || [])].find((trigger) => trigger.tabIndex >= 0 && isVisible(trigger));
  }

  function focusViewer(state, force = false) {
    if (!isOpen(state.modal) || (!force && state.modal.contains(document.activeElement))) return;
    focus(state.modal.querySelector('[data-zoom-modal-toggle]') || state.modal);
  }

  function transitionMilliseconds(modal) {
    const style = getComputedStyle(modal);
    const milliseconds = (value) => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000);
    const durations = style.transitionDuration.split(',').map(milliseconds);
    const delays = style.transitionDelay.split(',').map(milliseconds);
    return Math.max(0, ...durations.map((duration, index) => duration + delays[index % delays.length]));
  }

  function focusable(modal) {
    return [...modal.querySelectorAll('button, a[href], input, select, textarea, video[controls], [tabindex]:not([tabindex="-1"])')].filter((element) => {
      const slide = element.closest('[data-zoom-slide]');
      return !element.disabled && element.getAttribute('aria-disabled') !== 'true' && !element.closest('[inert]') && isVisible(element) && (!slide || slide.classList.contains('swiper-slide-active'));
    });
  }

  function sync(state) {
    const open = isOpen(state.modal);
    if (open === state.open) return;
    state.open = open;
    cancelAnimationFrame(state.focusFrame);
    clearTimeout(state.focusTimer);
    if (open) {
      const active = document.activeElement;
      state.returnTo = state.opener || (active instanceof HTMLElement && !state.modal.contains(active) ? active : null);
      state.focusFrame = requestAnimationFrame(() => {
        focusViewer(state, true);
      });
      // Visibility can still be hidden during the first animation frame. Retry once
      // after the CSS transition, without taking focus away from a viewer control.
      state.focusTimer = setTimeout(() => focusViewer(state), transitionMilliseconds(state.modal) + 50);
    } else {
      syncImageTriggers(state);
      const destination = isVisible(state.returnTo) && state.returnTo.tabIndex >= 0 ? state.returnTo : galleryOpener(state);
      state.opener = null;
      state.returnTo = null;
      state.focusFrame = requestAnimationFrame(() => focus(destination));
    }
  }

  function setup(modal) {
    if (states.has(modal)) return;
    const container = modal.closest('[data-zoom-container]');
    const state = { modal, container, open: false, opener: null, returnTo: null, focusFrame: 0, focusTimer: 0, pointer: null };
    modal.setAttribute('aria-label', strings.title);
    modal.setAttribute('tabindex', '-1');
    const closeButton = modal.querySelector('[data-zoom-modal-toggle]');
    if (closeButton) {
      closeButton.setAttribute('aria-label', strings.close);
      closeButton.setAttribute('type', 'button');
    }
    const hint = document.createElement('p');
    hint.className = 'ak-guided-lightbox-hint';
    hint.id = `${modal.id}-hint`;
    hint.textContent = strings.hint;
    modal.append(hint);
    modal.setAttribute('aria-describedby', hint.id);

    state.onClick = (event) => {
      if (!isOpen(modal) || (event.target !== modal && event.target !== modal.querySelector('.modal__body'))) return;
      const pointer = state.pointer;
      state.pointer = null;
      if (pointer && pointer.target === event.target && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) < 8) close(modal);
    };
    state.onPointerDown = (event) => {
      state.pointer = { target: event.target, x: event.clientX, y: event.clientY };
    };
    state.onTransitionEnd = (event) => {
      if (event.target === modal) focusViewer(state);
    };
    modal.addEventListener('click', state.onClick);
    modal.addEventListener('pointerdown', state.onPointerDown);
    modal.addEventListener('transitionend', state.onTransitionEnd);
    state.observer = new MutationObserver(() => sync(state));
    state.observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
    state.galleryObserver = new MutationObserver(() => syncImageTriggers(state));
    if (container) state.galleryObserver.observe(container, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    states.set(modal, state);
    syncImageTriggers(state);
    sync(state);
  }

  function cleanup(state) {
    state.observer.disconnect();
    state.galleryObserver.disconnect();
    cancelAnimationFrame(state.focusFrame);
    clearTimeout(state.focusTimer);
    state.modal.removeEventListener('click', state.onClick);
    state.modal.removeEventListener('pointerdown', state.onPointerDown);
    state.modal.removeEventListener('transitionend', state.onTransitionEnd);
    states.delete(state.modal);
  }

  function scan(root = document) {
    if (root instanceof Element && root.matches(modalSelector)) setup(root);
    root.querySelectorAll?.(modalSelector).forEach(setup);
    states.forEach((state) => { if (!state.modal.isConnected) cleanup(state); });
  }

  // Capture the actual opener before native gallery handlers show the modal.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || event.target.closest(modalSelector)) return;
    const trigger = event.target.closest(`${imageTriggerSelector}, [data-js-zoom-button], [data-zoom-media]`);
    const container = trigger?.closest('[data-zoom-container]');
    const state = states.get(container?.querySelector(modalSelector));
    if (state) state.opener = trigger.matches('button, a[href], [tabindex]') ? trigger : trigger.querySelector(imageTriggerSelector) || galleryOpener(state);
  }, true);

  document.addEventListener('keydown', (event) => {
    const state = [...states.values()].find((candidate) => isOpen(candidate.modal));
    if (!state) {
      const trigger = event.target instanceof Element && event.target.closest(imageTriggerSelector);
      if (trigger && trigger.tabIndex >= 0 && (event.key === 'Enter' || event.key === ' ') && !event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        if (!event.repeat) trigger.click();
      }
      return;
    }
    const { modal } = state;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      close(modal);
      return;
    }
    if (event.key === 'Tab') {
      const items = focusable(modal);
      const first = items[0] || modal;
      const last = items[items.length - 1] || modal;
      const active = document.activeElement;
      if (!modal.contains(active) || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        focus(event.shiftKey ? last : first);
      }
      return;
    }
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !event.altKey && !event.ctrlKey && !event.metaKey && !(event.target instanceof Element && event.target.closest('input, textarea, select, video, [contenteditable="true"], model-viewer'))) {
      const arrow = modal.querySelector(event.key === 'ArrowLeft' ? '.swiper-button-prev' : '.swiper-button-next');
      if (arrow && !arrow.disabled && arrow.getAttribute('aria-disabled') !== 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        arrow.click();
      }
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    const state = [...states.values()].find((candidate) => isOpen(candidate.modal));
    if (state && !state.modal.contains(event.target)) focus(state.modal.querySelector('[data-zoom-modal-toggle]') || state.modal);
  });

  // Theme-editor reloads and variant-rendered gallery replacements get fresh bindings.
  const replacements = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof Element && (node.matches(modalSelector) || node.querySelector(modalSelector))) scan(node);
    }));
    states.forEach((state) => { if (!state.modal.isConnected) cleanup(state); });
  });
  replacements.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => states.forEach((state) => {
    if (event.target.contains(state.modal)) cleanup(state);
  }));
  scan();
})();
